import React, {useMemo} from "react";
import {Accordion, AccordionDetails, AccordionSummary, Box, Grid, Tooltip, Typography, useTheme} from "@mui/material";
import {ExpandMore} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useFormContext} from "react-hook-form";
import {IRADAncillary, IRaDetailView} from "../../models/models";
import {LANGUAGES, LOCALES} from "../../config";
import {FormCheckbox, FormNumberInput, FormTextField} from "../../common/form-fields-wrappers";
import {formatCurrency, getLocaleByLang} from "../../utils";
import {useRecoilValue} from "recoil";
import {raDetailState} from "../state";
import EmptyState from "../../../../util/component/EmptyState";

const AncillariesInfo = () => {
	const theme = useTheme();
	const {t, i18n} = useTranslation(['network']);
	const raDetail = useRecoilValue(raDetailState);

	const raDetailForm = useFormContext<IRaDetailView>();

	const ancillariesFormValues = raDetailForm.getValues('ancillariesInfo');

	const ancillaryName = useMemo((): 'ancillaryNameIt' | 'ancillaryNameEn' => {
		let prop = 'ancillaryDefaultLabel';
		LANGUAGES.every((elem: string) => {
			if (i18n.language.includes(elem)) {
				prop = 'ancillaryName' + elem.charAt(0).toUpperCase() + elem.slice(1);
				return false;
			}
			return true;
		});

		return prop as 'ancillaryNameIt' | 'ancillaryNameEn';
	}, [i18n.language]);

	return (
		<>
			<Accordion>
				<AccordionSummary expandIcon={<ExpandMore />}>
					<Typography variant={"bodyLarge"}>
						{t('raDetail.ancillaries.accordion.title')}
					</Typography>
				</AccordionSummary>

				<AccordionDetails>
					<Grid container columnSpacing={2} sx={{mb: theme.spacing(4)}}>
						<Grid item xs={12}>
							<Typography variant={"bodyLarge"}>
								{t("raDetail.ancillaries.section.bookedAncillaries")}
							</Typography>
						</Grid>

						{
							ancillariesFormValues?.booked
								.map((ancillary: any, index: number) =>
									<AncillaryField key={`${ancillary.ancillaryCode}`} elem={ancillary} type={'booked'}
													index={index} ancillaryName={ancillaryName} disabled={true}/>
								)
						}

						{
							!ancillariesFormValues?.booked?.length &&
							<Grid item xs={12}>
								<EmptyState title={''} content={t('raDetail.ancillaries.message.noItems')}/>
							</Grid>
						}

						{
							!!ancillariesFormValues?.booked?.length &&
							<Grid item xs={12} sx={{pt: theme.spacing(2), display: "flex", justifyContent: "space-between"}}>
								<Typography variant={"body"} component={"span"}>
									{t("raDetail.ancillaries.amount.booked")}
								</Typography>

								<Typography variant={"bodyLarge"} component={"span"} sx={{mr: theme.spacing(2)}}>
									<AncillaryTotalCostInfo
										amount={raDetail?.quotationInfo.totAncillariesAmountBooked}
										currency={null}
									/>
								</Typography>
							</Grid>
						}
					</Grid>

					{/* Added Ancillaries */}
					<Grid container columnSpacing={2} sx={{mb: theme.spacing(4)}}>
						<Grid item xs={12}>
							<Typography variant={"bodyLarge"}>
								{t("raDetail.ancillaries.section.addedAncillaries")}
							</Typography>
						</Grid>

						{
							ancillariesFormValues?.counterSale?.length > 0 &&
								ancillariesFormValues?.counterSale
									.map((ancillary: any, index: number) =>
										<AncillaryField key={`${ancillary.ancillaryCode}`} elem={ancillary} type={'counterSale'} index={index} ancillaryName={ancillaryName} disabled={true}/>
									)
						}

						{
							!ancillariesFormValues?.counterSale?.length &&
							<Grid item xs={12}>
								<EmptyState title={''} content={t('raDetail.ancillaries.message.noItems')}/>
							</Grid>
						}

						{
							!!ancillariesFormValues?.counterSale?.length &&
							<Grid item xs={12} sx={{pt: theme.spacing(2), display: "flex", justifyContent: "space-between"}}>
								<Typography variant={"body"} component={"span"}>
									{t("raDetail.ancillaries.amount.added")}
								</Typography>
								<Typography variant={"body"} component={"span"} sx={{mr: theme.spacing(2)}}>
									<AncillaryTotalCostInfo
										amount={raDetail?.quotationInfo.addedAncillariesAmount}
										currency={null}
									/>
								</Typography>
							</Grid>
						}
					</Grid>

					{/* Total Amount */}
					{
						(!!ancillariesFormValues?.booked?.length || !!ancillariesFormValues?.counterSale?.length) &&
						<Grid container columnSpacing={2} sx={{pt: theme.spacing(2)}}>
							<Grid item xs={12} sx={{display: "flex", justifyContent: "space-between"}}>
								<Typography variant={"bodyLarge"} component={"span"}>
									{t("raDetail.ancillaries.amount.total")}
								</Typography>

								<Typography variant={"bodyLarge"} component={"span"} sx={{mr: theme.spacing(2)}}>
									<AncillaryTotalCostInfo
										amount={raDetail?.quotationInfo.totAncillariesAmount}
										currency={null}
										fontWeight={"bodyLarge"}
									/>
								</Typography>
							</Grid>
						</Grid>
					}
				</AccordionDetails>
			</Accordion>
		</>
	);
}

export default AncillariesInfo;

/**
 * Ancillary cost info - cost currency /unit
 */
const AncillaryCostInfo = ({elem}: {elem: IRADAncillary}) => {
	const theme = useTheme();
	const {i18n} = useTranslation(['network']);

	const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
		return getLocaleByLang(i18n.language);
	}, [i18n.language]);

	return (
		<>
			{
				<Typography variant={'body'} component={'span'} sx={{mx: theme.spacing(1)}}>
					{formatCurrency(locale as LOCALES, elem.amountTaxIncluded, elem?.ancillaryCurrency ?? undefined)}{elem.mileageNeeded ? '/km' : elem.maxUnits > 1 ? '/u' : ''}
				</Typography>
			}
		</>
	);
}

/**
 * Ancillary cost info - cost currency /unit
 */
export const AncillaryTotalCostInfo = ({amount, currency = null, fontWeight = 'body'}: {amount: number | null; currency: string | null; fontWeight?: 'body' | 'bodyLarge'}) => {
	const theme = useTheme();
	const {i18n} = useTranslation(['network']);

	const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
		return getLocaleByLang(i18n.language);
	}, [i18n.language]);

	return (
		<>
			{
				<Typography variant={fontWeight} component={'span'} sx={{mx: theme.spacing(1)}}>
					<strong>{formatCurrency(locale as LOCALES, amount, currency ?? undefined)}</strong>
				</Typography>
			}
		</>
	);
}

const AncillaryField = ({elem, type, index, ancillaryName, disabled = false}: {elem: IRADAncillary, type: 'booked' | 'counterSale', index: number, ancillaryName: 'ancillaryNameIt' | 'ancillaryNameEn', disabled: boolean}) => {
	const theme = useTheme();
	const raDetailForm = useFormContext<IRaDetailView>();

	if (elem.maxUnits > 1 || elem.mileageNeeded) {
		return (
			<Grid container item xs={12} key={`${type}_${elem.ancillaryCode}`} sx={{py: theme.spacing(1), mb: 'auto'}}>
				<Grid item xs={8} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start"}}>
					<Box sx={{width: '100px'}}>
						<FormNumberInput<IRaDetailView>
							name={`ancillariesInfo.${type}.${index}.${elem.mileageNeeded ? 'ancillaryMileage' : 'ancillaryUnits'}`}
							label={''}
							control={raDetailForm.control}
							minValue={0}
							maxValue={elem.mileageNeeded ? 999 : elem.maxUnits}
							disabled={disabled}
						/>
					</Box>
					<Tooltip title={elem[ancillaryName] as string} disableInteractive sx={{pt: theme.spacing(2)}}>
						<Typography variant={'body'} sx={{ml: theme.spacing(2), py: theme.spacing(1)}}>
							{elem[ancillaryName]}
						</Typography>
					</Tooltip>
				</Grid>
				<Grid item xs={4} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', py: theme.spacing(1), pr: theme.spacing(2)}}>
					<AncillaryCostInfo elem={elem}/>
				</Grid>

				{
					['DELIVERY', 'COLLECTION'].includes(elem.ancillarySubType as string) &&
					<Grid item xs={12} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start", pr: theme.spacing(2)}}>
						<FormTextField<IRaDetailView>
							name={`ancillariesInfo.${type}.${index}.ancillaryDeliveryCollectionAddress`}
							label={elem[ancillaryName]}
							control={raDetailForm.control}
							disabled={disabled}
						/>
					</Grid>
				}
			</Grid>
		)
	} else {
		return (
			<Grid container item xs={12} key={`${type}_${elem.ancillaryCode}`} sx={{py: theme.spacing(1)}}>
				<Grid item xs={8} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start", pt: theme.spacing(2)}}>
					<Tooltip title={elem.ancillaryDescr ?? ''} disableInteractive sx={{display: "flex", alignItems: "flex-end"}}>
						<span>
							<FormCheckbox<IRaDetailView>
								name={`ancillariesInfo.${type}.${index}.ancillaryUnits`}
								label={elem[ancillaryName]}
								disabled={disabled}
							/>
						</span>
					</Tooltip>
				</Grid>
				<Grid item xs={4} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', py: theme.spacing(1), pr: theme.spacing(2)}}>
					<AncillaryCostInfo elem={elem}/>
				</Grid>

				{
					['URBAN_DELIVERY', 'URBAN_COLLECTION'].includes(elem.ancillarySubType as string) &&
					<Grid item xs={12} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start", pr: theme.spacing(2)}}>
						<FormTextField<IRaDetailView>
							name={`ancillariesInfo.${type}.${index}.ancillaryDeliveryCollectionAddress`}
							label={elem[ancillaryName]}
							control={raDetailForm.control}
							disabled={disabled}
						/>
					</Grid>
				}
			</Grid>
		)
	}
}